import { useCallback, useState } from 'react';
import useChatDragDrop from '../../common/chat/composer/useChatDragDrop';
import { sendFile, sendImages } from '../../common/chat/shared/chatApi';
import {
  GROUP_CHAT_MAX_FILE_BYTES,
  isImageFile,
} from './artistGroupChatPaneUtils';

export default function useArtistGroupChatFileDrop({
  artistUserId,
  chatDropScopeRef,
  handleComposerSent,
  messagesContainerRef,
  readOnly,
  replyDraft,
  thread,
  threadId,
  toastError,
}) {
  const [uploadingDropFiles, setUploadingDropFiles] = useState(false);

  const handleDropFiles = useCallback(
    async (files = [], { rejectedCount = 0 } = {}) => {
      if (!threadId) {
        toastError('チャットを選択してください');
        return;
      }
      if (readOnly || thread?.status !== 'open') {
        toastError('現在このチャットでは送信できません');
        return;
      }
      const incoming = Array.from(files || []).filter(Boolean);
      if (!incoming.length) return;

      const oversized = incoming.filter(
        (file) => typeof file?.size === 'number' && file.size > GROUP_CHAT_MAX_FILE_BYTES
      ).length;
      const valid = incoming.filter(
        (file) => typeof file?.size !== 'number' || file.size <= GROUP_CHAT_MAX_FILE_BYTES
      );
      if (!valid.length) {
        toastError('100MBを超えるファイルは送信できません');
        return;
      }

      const images = valid.filter(isImageFile).slice(0, 5);
      const imageOverflow = Math.max(0, valid.filter(isImageFile).length - images.length);
      const filesOnly = valid.filter((file) => !isImageFile(file));
      const fileToSend = filesOnly[0] || null;
      const fileOverflow = Math.max(0, filesOnly.length - (fileToSend ? 1 : 0));

      setUploadingDropFiles(true);
      try {
        if (images.length > 0) {
          await sendImages({
            mode: 'artist',
            scope: 'group',
            groupThreadId: threadId,
            files: images,
            senderId: artistUserId,
            replyToMessageId: replyDraft?.messageId || '',
          });
        }
        if (fileToSend) {
          await sendFile({
            mode: 'artist',
            scope: 'group',
            groupThreadId: threadId,
            file: fileToSend,
            senderId: artistUserId,
            replyToMessageId: replyDraft?.messageId || '',
          });
        }
        await handleComposerSent();
        if (rejectedCount || oversized || imageOverflow || fileOverflow) {
          toastError('一部のファイルは制限により送信されませんでした');
        }
      } catch (error) {
        toastError(error?.message || 'ファイル送信に失敗しました');
      } finally {
        setUploadingDropFiles(false);
      }
    },
    [artistUserId, handleComposerSent, readOnly, replyDraft, thread?.status, threadId, toastError]
  );

  return useChatDragDrop({
    areaRef: messagesContainerRef,
    highlightRef: chatDropScopeRef,
    enabled: Boolean(threadId && thread?.status === 'open' && !readOnly && !uploadingDropFiles),
    onDrop: handleDropFiles,
    onInvalid: (message) => toastError(message || 'この操作は利用できません'),
    maxBytes: GROUP_CHAT_MAX_FILE_BYTES,
  });
}
